import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trash2, Plus } from "lucide-react";
import { categoryInfo } from "../constants";
import type { SKURulesState, NewGarmentInputs, SKURuleCategoryKey } from "../types";

const CATEGORY_LIST: SKURuleCategoryKey[] = [
  'standardGarments',
  'doubleSidedPrefixes',
  'firstTwoSegments',
  'wrapperTypes',
  'passThrough',
];

interface SkuRulesEditorProps {
  rules: SKURulesState;
  newGarmentInputs: NewGarmentInputs;
  setNewGarmentInputs: React.Dispatch<React.SetStateAction<NewGarmentInputs>>;
  addGarment: (category: SKURuleCategoryKey) => void;
  removeGarment: (category: keyof SKURulesState, garment: string) => void;
  isSaving: boolean;
  appendRuleValue: (category: SKURuleCategoryKey, value: string) => void;
  inventorySkus: string[];
  unusedByCategory: Record<SKURuleCategoryKey, Set<string>>;
  removeUnusedInCategory: (category: SKURuleCategoryKey) => void;
}

export const SkuRulesEditor: React.FC<SkuRulesEditorProps> = ({
  rules,
  newGarmentInputs,
  setNewGarmentInputs,
  addGarment,
  removeGarment,
  isSaving,
  appendRuleValue,
  inventorySkus,
  unusedByCategory,
  removeUnusedInCategory,
}) => {
  const [searchTerms, setSearchTerms] = useState<Record<SKURuleCategoryKey, string>>({
    standardGarments: '',
    doubleSidedPrefixes: '',
    firstTwoSegments: '',
    wrapperTypes: '',
    passThrough: '',
  });

  const deriveRuleValue = (category: SKURuleCategoryKey, sku: string): string | null => {
    const parts = sku.split('-');
    if (!parts.length) return null;

    const base = parts[0];
    if (!base) return null;

    switch (category) {
      case 'doubleSidedPrefixes': {
        return base.startsWith('2') ? base : `2${base}`;
      }
      case 'standardGarments':
      case 'firstTwoSegments':
      case 'wrapperTypes':
      case 'passThrough':
      default:
        return base;
    }
  };

  const matchesByCategory = useMemo(() => {
    const MAX_PREVIEW = 30;
    const result = {} as Record<
      SKURuleCategoryKey,
      {
        matchedSkus: string[];
        pendingRuleValues: string[];
        totalMatches: number;
      }
    >;

    CATEGORY_LIST.forEach((category) => {
      const term = searchTerms[category].trim().toUpperCase();
      if (!term) {
        result[category] = { matchedSkus: [], pendingRuleValues: [], totalMatches: 0 };
        return;
      }

      const matchedSkus = inventorySkus
        .filter((sku) => sku.includes(term))
        .filter((sku) => {
          const value = deriveRuleValue(category, sku);
          return Boolean(value);
        });

      const limitedSkus = matchedSkus.slice(0, MAX_PREVIEW);
      const uniqueRuleValues = Array.from(
        new Set(
          matchedSkus
            .map((sku) => deriveRuleValue(category, sku))
            .filter((value): value is string => Boolean(value))
        )
      );

      const existing = new Set(rules[category] || []);
      const pendingRuleValues = uniqueRuleValues.filter((value) => !existing.has(value));

      result[category] = {
        matchedSkus: limitedSkus,
        pendingRuleValues,
        totalMatches: matchedSkus.length,
      };
    });

    return result;
  }, [inventorySkus, rules, searchTerms]);

  const bulkAddRuleValues = (category: SKURuleCategoryKey) => {
    matchesByCategory[category].pendingRuleValues.forEach((value) => {
      appendRuleValue(category, value);
    });
  };

  const renderChips = (category: SKURuleCategoryKey) => {
    const items = rules[category] || [];
    const unused = unusedByCategory[category];
    const unusedCount = items.filter((g) => unused.has(g)).length;

    return (
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {items.map((garment) => {
          const isUnused = unused.has(garment);
          return (
            <Badge
              key={garment}
              variant="secondary"
              className={`flex items-center gap-1 ${isUnused ? 'opacity-40 line-through' : ''}`}
              title={isUnused ? 'No inventory SKUs match this prefix' : undefined}
            >
              {garment}
              <Button
                size="sm"
                variant="ghost"
                className="h-4 w-4 p-0 hover:bg-red-100"
                onClick={() => removeGarment(category, garment)}
                disabled={isSaving}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </Badge>
          );
        })}
        {unusedCount > 0 && (
          <Button
            size="sm"
            variant="outline"
            className="h-6 px-2 text-xs text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
            onClick={() => removeUnusedInCategory(category)}
            disabled={isSaving}
          >
            <Trash2 className="h-3 w-3 mr-1" />
            Remove {unusedCount} unused
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="grid gap-6">
      {/* Standard Garments */}
      <Card className={categoryInfo.standardGarments.color}>
        <CardHeader>
          <CardTitle>{categoryInfo.standardGarments.title}</CardTitle>
          <CardDescription>
            {categoryInfo.standardGarments.description}
            <br />
            <code className="text-xs bg-white/50 px-1 rounded">
              {categoryInfo.standardGarments.example}
            </code>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {renderChips('standardGarments')}
          <div className="flex gap-2">
            <Input
              placeholder="Add new garment (e.g., PYJ)"
              value={newGarmentInputs.standardGarments}
              onChange={(e) => setNewGarmentInputs(prev => ({
                ...prev,
                standardGarments: e.target.value
              }))}
              onKeyPress={(e) => e.key === 'Enter' && addGarment('standardGarments')}
              disabled={isSaving}
            />
            <Button onClick={() => addGarment('standardGarments')} disabled={isSaving}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          <div className="mt-4 border rounded-md bg-white/60 p-3 space-y-3">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-3">
              <Input
                placeholder="Search inventory SKUs (e.g., TEE)"
                value={searchTerms.standardGarments}
                onChange={(e) =>
                  setSearchTerms((prev) => ({ ...prev, standardGarments: e.target.value.toUpperCase() }))
                }
                disabled={isSaving}
              />
              <Button
                variant="secondary"
                onClick={() => {
                  bulkAddRuleValues('standardGarments');
                  setSearchTerms((prev) => ({ ...prev, standardGarments: '' }));
                }}
                disabled={isSaving || matchesByCategory.standardGarments.pendingRuleValues.length === 0}
                className="whitespace-nowrap"
              >
                {matchesByCategory.standardGarments.pendingRuleValues.length > 0
                  ? `Add ${matchesByCategory.standardGarments.pendingRuleValues.length} Rule${
                      matchesByCategory.standardGarments.pendingRuleValues.length > 1 ? 's' : ''
                    }`
                  : 'Add Rules'}
              </Button>
            </div>
            {searchTerms.standardGarments.trim() && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Found {matchesByCategory.standardGarments.totalMatches} SKU
                  {matchesByCategory.standardGarments.totalMatches !== 1 ? 's' : ''}. Pending rule values:{' '}
                  {matchesByCategory.standardGarments.pendingRuleValues.length > 0
                    ? matchesByCategory.standardGarments.pendingRuleValues.join(', ')
                    : 'None'}
                </p>
                {matchesByCategory.standardGarments.pendingRuleValues.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {matchesByCategory.standardGarments.pendingRuleValues.map((value) => (
                      <Button
                        key={value}
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-xs"
                        onClick={() => appendRuleValue('standardGarments', value)}
                        disabled={isSaving}
                      >
                        Add {value}
                      </Button>
                    ))}
                  </div>
                )}
                <div className="max-h-32 overflow-auto flex flex-wrap gap-2">
                  {matchesByCategory.standardGarments.matchedSkus.map((sku) => (
                    <Badge key={`std-${sku}`} variant="outline" className="font-mono text-xs">
                      {sku}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Double-Sided Prefixes */}
      <Card className={categoryInfo.doubleSidedPrefixes.color}>
        <CardHeader>
          <CardTitle>{categoryInfo.doubleSidedPrefixes.title}</CardTitle>
          <CardDescription>
            {categoryInfo.doubleSidedPrefixes.description}
            <br />
            <code className="text-xs bg-white/50 px-1 rounded">
              {categoryInfo.doubleSidedPrefixes.example}
            </code>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {renderChips('doubleSidedPrefixes')}
          <div className="flex gap-2">
            <Input
              placeholder="Add new prefix (e.g., 2PYJ)"
              value={newGarmentInputs.doubleSidedPrefixes}
              onChange={(e) => setNewGarmentInputs(prev => ({
                ...prev,
                doubleSidedPrefixes: e.target.value
              }))}
              onKeyPress={(e) => e.key === 'Enter' && addGarment('doubleSidedPrefixes')}
              disabled={isSaving}
            />
            <Button onClick={() => addGarment('doubleSidedPrefixes')} disabled={isSaving}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          <div className="mt-4 border rounded-md bg-white/60 p-3 space-y-2">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-3">
              <Input
                placeholder="Search SKUs (e.g., 2TEE)"
                value={searchTerms.doubleSidedPrefixes}
                onChange={(e) =>
                  setSearchTerms((prev) => ({ ...prev, doubleSidedPrefixes: e.target.value.toUpperCase() }))
                }
                disabled={isSaving}
              />
              <Button
                variant="secondary"
                onClick={() => {
                  bulkAddRuleValues('doubleSidedPrefixes');
                  setSearchTerms((prev) => ({ ...prev, doubleSidedPrefixes: '' }));
                }}
                disabled={isSaving || matchesByCategory.doubleSidedPrefixes.pendingRuleValues.length === 0}
                className="whitespace-nowrap"
              >
                {matchesByCategory.doubleSidedPrefixes.pendingRuleValues.length > 0
                  ? `Add ${matchesByCategory.doubleSidedPrefixes.pendingRuleValues.length} Rule${
                      matchesByCategory.doubleSidedPrefixes.pendingRuleValues.length > 1 ? 's' : ''
                    }`
                  : 'Add Rules'}
              </Button>
            </div>
            {searchTerms.doubleSidedPrefixes.trim() && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Found {matchesByCategory.doubleSidedPrefixes.totalMatches} SKU
                  {matchesByCategory.doubleSidedPrefixes.totalMatches !== 1 ? 's' : ''}. Pending rule values:{' '}
                  {matchesByCategory.doubleSidedPrefixes.pendingRuleValues.length > 0
                    ? matchesByCategory.doubleSidedPrefixes.pendingRuleValues.join(', ')
                    : 'None'}
                </p>
                {matchesByCategory.doubleSidedPrefixes.pendingRuleValues.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {matchesByCategory.doubleSidedPrefixes.pendingRuleValues.map((value) => (
                      <Button
                        key={value}
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-xs"
                        onClick={() => appendRuleValue('doubleSidedPrefixes', value)}
                        disabled={isSaving}
                      >
                        Add {value}
                      </Button>
                    ))}
                  </div>
                )}
                <div className="max-h-32 overflow-auto flex flex-wrap gap-2">
                  {matchesByCategory.doubleSidedPrefixes.matchedSkus.map((sku) => (
                    <Badge key={`dbl-${sku}`} variant="outline" className="font-mono text-xs">
                      {sku}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* First Two Segments */}
      <Card className={categoryInfo.firstTwoSegments.color}>
        <CardHeader>
          <CardTitle>{categoryInfo.firstTwoSegments.title}</CardTitle>
          <CardDescription>
            {categoryInfo.firstTwoSegments.description}
            <br />
            <code className="text-xs bg-white/50 px-1 rounded">
              {categoryInfo.firstTwoSegments.example}
            </code>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {renderChips('firstTwoSegments')}
          <div className="flex gap-2">
            <Input
              placeholder="Add new first two segments"
              value={newGarmentInputs.firstTwoSegments}
              onChange={(e) => setNewGarmentInputs(prev => ({
                ...prev,
                firstTwoSegments: e.target.value
              }))}
              onKeyPress={(e) => e.key === 'Enter' && addGarment('firstTwoSegments')}
              disabled={isSaving}
            />
            <Button onClick={() => addGarment('firstTwoSegments')} disabled={isSaving}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          <div className="mt-4 border rounded-md bg-white/60 p-3 space-y-2">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-3">
              <Input
                placeholder="Search SKUs (e.g., CSH)"
                value={searchTerms.firstTwoSegments}
                onChange={(e) =>
                  setSearchTerms((prev) => ({ ...prev, firstTwoSegments: e.target.value.toUpperCase() }))
                }
                disabled={isSaving}
              />
              <Button
                variant="secondary"
                onClick={() => {
                  bulkAddRuleValues('firstTwoSegments');
                  setSearchTerms((prev) => ({ ...prev, firstTwoSegments: '' }));
                }}
                disabled={isSaving || matchesByCategory.firstTwoSegments.pendingRuleValues.length === 0}
                className="whitespace-nowrap"
              >
                {matchesByCategory.firstTwoSegments.pendingRuleValues.length > 0
                  ? `Add ${matchesByCategory.firstTwoSegments.pendingRuleValues.length} Rule${
                      matchesByCategory.firstTwoSegments.pendingRuleValues.length > 1 ? 's' : ''
                    }`
                  : 'Add Rules'}
              </Button>
            </div>
            {searchTerms.firstTwoSegments.trim() && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Found {matchesByCategory.firstTwoSegments.totalMatches} SKU
                  {matchesByCategory.firstTwoSegments.totalMatches !== 1 ? 's' : ''}. Pending rule values:{' '}
                  {matchesByCategory.firstTwoSegments.pendingRuleValues.length > 0
                    ? matchesByCategory.firstTwoSegments.pendingRuleValues.join(', ')
                    : 'None'}
                </p>
                {matchesByCategory.firstTwoSegments.pendingRuleValues.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {matchesByCategory.firstTwoSegments.pendingRuleValues.map((value) => (
                      <Button
                        key={value}
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-xs"
                        onClick={() => appendRuleValue('firstTwoSegments', value)}
                        disabled={isSaving}
                      >
                        Add {value}
                      </Button>
                    ))}
                  </div>
                )}
                <div className="max-h-32 overflow-auto flex flex-wrap gap-2">
                  {matchesByCategory.firstTwoSegments.matchedSkus.map((sku) => (
                    <Badge key={`fts-${sku}`} variant="outline" className="font-mono text-xs">
                      {sku}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Wrapper Types and Pass Through */}
      {(['wrapperTypes', 'passThrough'] as const).map(category => (
        <Card key={category} className={categoryInfo[category].color}>
          <CardHeader>
            <CardTitle>{categoryInfo[category].title}</CardTitle>
            <CardDescription>
              {categoryInfo[category].description}
              <br />
              <code className="text-xs bg-white/50 px-1 rounded">
                {categoryInfo[category].example}
              </code>
            </CardDescription>
          </CardHeader>
          <CardContent>
            {renderChips(category)}
            <div className="flex gap-2">
              <Input
                placeholder={`Add new ${category === 'wrapperTypes' ? 'wrapper' : 'pass-through'}`}
                value={newGarmentInputs[category]}
                onChange={(e) => setNewGarmentInputs(prev => ({
                  ...prev,
                  [category]: e.target.value
                }))}
                onKeyPress={(e) => e.key === 'Enter' && addGarment(category)}
                disabled={isSaving}
              />
              <Button onClick={() => addGarment(category)} disabled={isSaving}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            <div className="mt-4 border rounded-md bg-white/60 p-3 space-y-2">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-3">
                <Input
                  placeholder={`Search SKUs (e.g., ${category === 'wrapperTypes' ? 'CUST' : 'AF'})`}
                  value={searchTerms[category]}
                  onChange={(e) =>
                    setSearchTerms((prev) => ({ ...prev, [category]: e.target.value.toUpperCase() }))
                  }
                  disabled={isSaving}
                />
                <Button
                  variant="secondary"
                  onClick={() => {
                    bulkAddRuleValues(category);
                    setSearchTerms((prev) => ({ ...prev, [category]: '' }));
                  }}
                  disabled={isSaving || matchesByCategory[category].pendingRuleValues.length === 0}
                  className="whitespace-nowrap"
                >
                  {matchesByCategory[category].pendingRuleValues.length > 0
                    ? `Add ${matchesByCategory[category].pendingRuleValues.length} Rule${
                        matchesByCategory[category].pendingRuleValues.length > 1 ? 's' : ''
                      }`
                    : 'Add Rules'}
                </Button>
              </div>
              {searchTerms[category].trim() && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Found {matchesByCategory[category].totalMatches} SKU
                    {matchesByCategory[category].totalMatches !== 1 ? 's' : ''}. Pending rule values:{' '}
                    {matchesByCategory[category].pendingRuleValues.length > 0
                      ? matchesByCategory[category].pendingRuleValues.join(', ')
                      : 'None'}
                  </p>
                  {matchesByCategory[category].pendingRuleValues.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {matchesByCategory[category].pendingRuleValues.map((value) => (
                        <Button
                          key={value}
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-xs"
                          onClick={() => appendRuleValue(category, value)}
                          disabled={isSaving}
                        >
                          Add {value}
                        </Button>
                      ))}
                    </div>
                  )}
                  <div className="max-h-32 overflow-auto flex flex-wrap gap-2">
                    {matchesByCategory[category].matchedSkus.map((sku) => (
                      <Badge key={`${category}-${sku}`} variant="outline" className="font-mono text-xs">
                        {sku}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}

    </div>
  );
};
